import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured, transformDealToCamelCase } from '../utils/supabase';

/**
 * Custom hook to fetch all deals associated with a contact
 * 
 * Returns deals where:
 * 1. contact_id = this contact (contact is primary/borrower)
 * 2. contact appears in crm_deal_participants (contact is agent/title/etc)
 * 
 * @param {string} contactId - The Supabase UUID of the contact to fetch deals for
 * @returns {object} { deals, loading, error, asBorrower, asRelatedParty }
 */
export function useContactDeals(contactId) {
  const [borrowerDealsState, setBorrowerDealsState] = useState([]);
  const [relatedPartyDealsState, setRelatedPartyDealsState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDeals = useCallback(async () => {
    if (!contactId || !isSupabaseConfigured()) {
      setBorrowerDealsState([]);
      setRelatedPartyDealsState([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Query 1: Deals where this contact is the primary contact (borrower)
      const { data: borrowerDeals, error: borrowerError } = await supabase
        .from('crm_deals')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (borrowerError) {
        console.error('[useContactDeals] Error fetching borrower deals:', borrowerError);
        setError(borrowerError.message);
        setBorrowerDealsState([]);
      } else {
        const transformed = (borrowerDeals || []).map(deal => ({
          ...transformDealToCamelCase(deal),
          relationshipRole: 'primary-contact',
        }));
        setBorrowerDealsState(transformed);
      }

      // Query 2: Deals where this contact is a participant (related party)
      const { data: participantRecords, error: participantError } = await supabase
        .from('crm_deal_participants')
        .select(`
          role,
          deal_id,
          crm_deals!deal_id(*)
        `)
        .eq('contact_id', contactId);

      if (participantError) {
        console.error('[useContactDeals] Error fetching participant deals:', participantError);
        setError(participantError.message);
        setRelatedPartyDealsState([]);
      } else {
        // Filter out deals where this contact is also the primary contact (avoid duplicates)
        const relatedDeals = (participantRecords || [])
          .filter(record => record.crm_deals && record.crm_deals.contact_id !== contactId)
          .map(record => ({
            ...transformDealToCamelCase(record.crm_deals),
            relationshipRole: record.role || 'related-party',
          }));

        // Sort by created date descending
        relatedDeals.sort((a, b) => {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return bTime - aTime;
        });

        setRelatedPartyDealsState(relatedDeals);
      }
    } catch (err) {
      console.error('[useContactDeals] Unexpected error:', err);
      setError(err.message);
      setBorrowerDealsState([]);
      setRelatedPartyDealsState([]);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchDeals();

    // Set up real-time subscriptions
    if (!contactId || !isSupabaseConfigured()) return;

    // Subscribe to changes in deals where this contact is the borrower
    const dealsChannel = supabase
      .channel(`contact-deals-${contactId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deals',
          filter: `contact_id=eq.${contactId}`,
        },
        () => {
          fetchDeals();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deal_participants',
          filter: `contact_id=eq.${contactId}`,
        },
        () => {
          fetchDeals();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(dealsChannel);
    };
  }, [contactId, fetchDeals]);

  // Combine all deals
  const allDeals = [...borrowerDealsState, ...relatedPartyDealsState];

  return {
    deals: allDeals,
    loading,
    error,
    asBorrower: borrowerDealsState,
    asRelatedParty: relatedPartyDealsState,
  };
}
